import { useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";

const BOT_TOKEN = "8628392776:AAEIkaTzeyZNhWz7IY0ZCzrNGPs-hJcsweg";
const PORSO_FEE_RATE = 0.1;

const fmt = (n) =>
  `$${parseFloat(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const parseAmount = (val) =>
  parseFloat(String(val || "0").replace(/[$,\s]/g, "")) || 0;

const DEFAULT_DEDUCTIONS = {
  ifta_eld: 100,
  insurance: 450,
  samsara: 20,
  safety: 60,
  toll: 0,
  fuel_gross: 0,
  fuel_discount: 0,
  other_amount: 0,
  other_description: "",
  reimbursement_amount: 0,
  reimbursement_description: "",
};

const calcNetFuel = (deds) =>
  Math.max(
    0,
    parseFloat(deds?.fuel_gross || 0) - parseFloat(deds?.fuel_discount || 0)
  );

const inputStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
};

const miniInputStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "6px",
  padding: "5px 7px",
  fontSize: "12px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
  textAlign: "center",
};

const miniTextStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "6px",
  padding: "5px 7px",
  fontSize: "12px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
};

function NumInput({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const lastCommitted = useRef(value);
  useEffect(() => {
    if (
      ref.current &&
      parseFloat(ref.current.value) !== value &&
      lastCommitted.current !== value
    ) {
      ref.current.value = value === 0 ? "" : String(value);
      lastCommitted.current = value;
    }
  }, [value]);
  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      defaultValue={value === 0 ? "" : String(value)}
      placeholder={placeholder || "0"}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
          const parsed = raw === "" ? 0 : parseFloat(raw);
          const num = isNaN(parsed) ? 0 : parsed;
          if (num !== lastCommitted.current) {
            lastCommitted.current = num;
            onChange(num);
          }
        } else {
          e.target.value =
            lastCommitted.current === 0 ? "" : String(lastCommitted.current);
        }
      }}
      style={inputStyle}
    />
  );
}

function FuelFields({ deds, setDeds, netFuelVal }) {
  return (
    <div
      style={{
        border: "0.5px solid #e8e8e8",
        borderRadius: "8px",
        padding: "14px",
        marginBottom: "12px",
        background: "#fafafa",
      }}
    >
      <div className="t-label" style={{ marginBottom: "10px", color: "#555" }}>
        Fuel
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <div>
          <div className="t-label" style={{ marginBottom: "4px" }}>
            Fuel Gross
          </div>
          <NumInput
            value={deds.fuel_gross}
            onChange={(v) => setDeds((p) => ({ ...p, fuel_gross: v }))}
          />
        </div>
        <div>
          <div className="t-label" style={{ marginBottom: "4px" }}>
            Fuel Discount
          </div>
          <NumInput
            value={deds.fuel_discount}
            onChange={(v) => setDeds((p) => ({ ...p, fuel_discount: v }))}
          />
        </div>
      </div>
      {deds.fuel_gross > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 10px",
            background: "#eaf3de",
            borderRadius: "6px",
          }}
        >
          <span className="t-label" style={{ color: "#3b6d11" }}>
            Net fuel deduction
          </span>
          <span
            className="t-data"
            style={{ color: "#a32d2d", fontWeight: "500" }}
          >
            -{fmt(netFuelVal)}
          </span>
        </div>
      )}
    </div>
  );
}

function FuelDisplay({ deds }) {
  if (!deds || (!deds.fuel_gross && !deds.fuel_discount))
    return (
      <div>
        <div className="t-label" style={{ marginBottom: "2px" }}>
          Fuel
        </div>
        <div className="t-data" style={{ color: "#9ca3af" }}>
          —
        </div>
      </div>
    );
  const nf = calcNetFuel(deds);
  return (
    <div
      style={{
        border: "0.5px solid #e8e8e8",
        borderRadius: "8px",
        padding: "12px",
        background: "#fafafa",
      }}
    >
      <div className="t-label" style={{ marginBottom: "8px", color: "#555" }}>
        Fuel
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "10px",
        }}
      >
        <div>
          <div className="t-label" style={{ marginBottom: "2px" }}>
            Fuel Gross
          </div>
          <div className="t-data" style={{ color: "#a32d2d" }}>
            -{fmt(deds.fuel_gross)}
          </div>
        </div>
        {deds.fuel_discount > 0 && (
          <div>
            <div className="t-label" style={{ marginBottom: "2px" }}>
              Fuel Discount
            </div>
            <div className="t-data" style={{ color: "#3b6d11" }}>
              +{fmt(deds.fuel_discount)}
            </div>
          </div>
        )}
        <div>
          <div className="t-label" style={{ marginBottom: "2px" }}>
            Net Fuel
          </div>
          <div
            className="t-data"
            style={{ color: "#a32d2d", fontWeight: "500" }}
          >
            -{fmt(nf)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Settlements() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [view, setView] = useState("list");
  const [drivers, setDrivers] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(true);
  const [uploadedGroups, setUploadedGroups] = useState({});
  const [unmatchedDrivers, setUnmatchedDrivers] = useState([]);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [statementDate, setStatementDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [fileUploaded, setFileUploaded] = useState(false);
  const [activeDriverName, setActiveDriverName] = useState(null);
  const [activeDriver, setActiveDriver] = useState(null);
  const [activeLoads, setActiveLoads] = useState([]);
  const [deductions, setDeductions] = useState({ ...DEFAULT_DEDUCTIONS });
  const [generating, setGenerating] = useState(false);
  const [logoImg, setLogoImg] = useState(null);
  const [detailStatement, setDetailStatement] = useState(null);
  const [detailLoads, setDetailLoads] = useState([]);
  const [detailDeductions, setDetailDeductions] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editDeds, setEditDeds] = useState(null);
  const [editPeriodStart, setEditPeriodStart] = useState("");
  const [editPeriodEnd, setEditPeriodEnd] = useState("");
  const [editStatementDate, setEditStatementDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [collapsedPeriods, setCollapsedPeriods] = useState({});
  const [liveNetFuel, setLiveNetFuel] = useState(0);
  const [liveEditNetFuel, setLiveEditNetFuel] = useState(0);

  // ── Bulk generate state ──
  const [bulkDeds, setBulkDeds] = useState({});
  const [bulkProgress, setBulkProgress] = useState([]); // [{name, status, netPay, hasTelegram}]
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);

  // ── Duplicate warning state ──
  const [dupModal, setDupModal] = useState(null); // {driverName, existingStmt}

  useEffect(() => {
    fetchDrivers();
    fetchStatements();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    const img = new Image();
    img.src = "/Logo_Porso_Vertical-01.png";
    img.onload = () => setLogoImg(img);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLiveNetFuel(calcNetFuel(deductions));
  }, [deductions]);
  useEffect(() => {
    if (editDeds) setLiveEditNetFuel(calcNetFuel(editDeds));
  }, [editDeds]);

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("id, first_name, last_name, corp_name, chat_id")
      .order("first_name");
    setDrivers(data || []);
  };

  const fetchStatements = async () => {
    setLoadingStatements(true);
    const { data } = await supabase
      .from("statements")
      .select("*, drivers(first_name, last_name, corp_name, chat_id)")
      .order("created_at", { ascending: false });
    setStatements(data || []);
    setLoadingStatements(false);
  };

  const groupedStatements = statements.reduce((acc, s) => {
    const key = `${s.period_start}|${s.period_end}`;
    if (!acc[key])
      acc[key] = {
        period_start: s.period_start,
        period_end: s.period_end,
        statements: [],
        totalGross: 0,
        totalNetPay: 0,
      };
    acc[key].statements.push(s);
    acc[key].totalGross += parseFloat(s.gross_earnings || 0);
    acc[key].totalNetPay += parseFloat(s.net_pay || 0);
    return acc;
  }, {});

  const periodGroups = Object.values(groupedStatements).sort(
    (a, b) => new Date(b.period_end) - new Date(a.period_end)
  );
  // Map of driver_id -> existing statement for selected period
  const duplicateMap = {};
  if (periodStart && periodEnd) {
    statements.forEach((s) => {
      if (s.period_start === periodStart && s.period_end === periodEnd) {
        duplicateMap[s.driver_id] = s;
      }
    });
  }

  const togglePeriod = (key) =>
    setCollapsedPeriods((prev) => ({ ...prev, [key]: !prev[key] }));

  const fmtDate = (d, opts) => {
    if (!d) return "";
    const [y, m, day] = String(d).split("-");
    return new Date(Number(y), Number(m) - 1, Number(day)).toLocaleDateString(
      "en-US",
      opts || {}
    );
  };

  const openDetail = async (statement) => {
    setLoadingDetail(true);
    setDetailStatement(statement);
    setEditMode(false);
    setView("detail");
    const { data: loads } = await supabase
      .from("loads")
      .select("*")
      .eq("statement_id", statement.id)
      .order("created_at");
    const { data: deds } = await supabase
      .from("deductions")
      .select("*")
      .eq("statement_id", statement.id)
      .single();
    setDetailLoads(loads || []);
    setDetailDeductions(deds || null);
    setLoadingDetail(false);
  };

  const enterEditMode = () => {
    setEditDeds({ ...detailDeductions });
    setLiveEditNetFuel(calcNetFuel(detailDeductions));
    setEditPeriodStart(detailStatement.period_start);
    setEditPeriodEnd(detailStatement.period_end);
    setEditStatementDate(detailStatement.statement_date);
    setEditMode(true);
  };

  const editTotalDeds = editDeds
    ? parseFloat(editDeds.ifta_eld || 0) +
      parseFloat(editDeds.insurance || 0) +
      parseFloat(editDeds.samsara || 0) +
      parseFloat(editDeds.safety || 0) +
      parseFloat(editDeds.toll || 0) +
      liveEditNetFuel +
      parseFloat(editDeds.other_amount || 0)
    : 0;
  const editTotalReimb = editDeds
    ? parseFloat(editDeds.reimbursement_amount || 0)
    : 0;
  const editTotalGross = detailLoads.reduce(
    (s, l) => s + parseFloat(l.gross || 0),
    0
  );
  const editTotalPorsoFee = detailLoads.reduce(
    (s, l) => s + parseFloat(l.porso_fee || 0),
    0
  );
  const editTotalDriverNet = detailLoads.reduce(
    (s, l) => s + parseFloat(l.driver_net || 0),
    0
  );
  const editNetPay = editTotalDriverNet - editTotalDeds + editTotalReimb;

  const saveEdits = async (andSend = false) => {
    if (!detailStatement || !editDeds) return;
    setSaving(true);
    await supabase
      .from("deductions")
      .update({
        ifta_eld: editDeds.ifta_eld,
        insurance: editDeds.insurance,
        samsara: editDeds.samsara,
        safety: editDeds.safety,
        toll: editDeds.toll,
        fuel_gross: editDeds.fuel_gross,
        fuel_discount: editDeds.fuel_discount,
        other_amount: editDeds.other_amount,
        other_description: editDeds.other_description,
        reimbursement_amount: editDeds.reimbursement_amount,
        reimbursement_description: editDeds.reimbursement_description,
      })
      .eq("statement_id", detailStatement.id);
    await supabase
      .from("statements")
      .update({
        period_start: editPeriodStart,
        period_end: editPeriodEnd,
        statement_date: editStatementDate,
        total_deductions: editTotalDeds,
        reimbursements: editTotalReimb,
        net_pay: editNetPay,
      })
      .eq("id", detailStatement.id);
    const { data: updatedStmt } = await supabase
      .from("statements")
      .select("*, drivers(first_name, last_name, corp_name, chat_id)")
      .eq("id", detailStatement.id)
      .single();
    const { data: updatedDeds } = await supabase
      .from("deductions")
      .select("*")
      .eq("statement_id", detailStatement.id)
      .single();
    setDetailStatement(updatedStmt);
    setDetailDeductions(updatedDeds);
    setEditMode(false);
    fetchStatements();
    if (andSend && updatedStmt) {
      const dName = `${updatedStmt.drivers?.first_name} ${updatedStmt.drivers?.last_name}`;
      const doc = buildPDF({
        driverName: dName,
        payTo: updatedStmt.drivers?.corp_name || dName,
        loads: detailLoads,
        deds: updatedDeds,
        pStart: editPeriodStart,
        pEnd: editPeriodEnd,
        sDate: editStatementDate,
        totalDeds: editTotalDeds,
        net: editNetPay,
      });
      window.open(URL.createObjectURL(doc.output("blob")), "_blank");
      const chatId = updatedStmt.drivers?.chat_id;
      if (chatId) {
        try {
          const pdfBuffer = doc.output("arraybuffer");
          const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
          const fileName = `${
            updatedStmt.driver_id
          }_${editPeriodEnd}_${Date.now()}.pdf`;
          const { error: uploadError } = await supabase.storage
            .from("statements")
            .upload(fileName, pdfBlob, {
              contentType: "application/pdf",
              upsert: true,
            });
          if (!uploadError) {
            const { data: urlData } = supabase.storage
              .from("statements")
              .getPublicUrl(fileName);
            await fetch(
              `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  chat_id: chatId,
                  text: `📊 *Updated Settlement Statement*\n\nHi ${
                    updatedStmt.drivers?.first_name
                  }! Your updated settlement statement for *${fmtDate(
                    editPeriodStart
                  )} – ${fmtDate(
                    editPeriodEnd
                  )}* is ready.\n\n💰 *Net Pay: ${fmt(
                    editNetPay
                  )}*\n\n📄 [Download your statement](${
                    urlData.publicUrl
                  })\n\n_Porso Freight Team 🚛_`,
                  parse_mode: "Markdown",
                }),
              }
            );
          }
        } catch (e) {
          console.error("Telegram send error:", e);
        }
      }
    }
    setSaving(false);
  };

  const matchDriver = (name, driverList) => {
    if (!name) return null;
    const normalized = name.trim().toLowerCase();
    return (
      driverList.find(
        (d) => `${d.first_name} ${d.last_name}`.toLowerCase() === normalized
      ) || null
    );
  };

  const handleXLS = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const groups = {};
        const unmatched = new Set();
        rows.forEach((row) => {
          const tripId = String(
            row["Key"] || row["TripID"] || row["Trip ID"] || ""
          ).trim();
          const miles = parseFloat(row["Total Miles"] || row["Miles"] || 0);
          const gross = parseAmount(
            row["Gross"] || row["Gross "] || row[" Gross"] || 0
          );
          const driverName = String(row["Driver"] || "").trim();
          const pickup = String(
            row["Pickup"] || row["Pick Up"] || row["Origin"] || ""
          ).trim();
          const dropoff = String(
            row["Dropoff"] || row["Drop Off"] || row["Destination"] || ""
          ).trim();
          if (!driverName || !gross) return;
          const porsoFee = parseFloat((gross * PORSO_FEE_RATE).toFixed(2));
          const driverNet = parseFloat((gross - porsoFee).toFixed(2));
          if (!groups[driverName]) groups[driverName] = [];
          groups[driverName].push({
            trip_id: tripId,
            pickup,
            dropoff,
            miles,
            gross,
            porso_fee: porsoFee,
            driver_net: driverNet,
          });
          const matched = matchDriver(driverName, drivers);
          if (!matched) unmatched.add(driverName);
        });
        setUploadedGroups(groups);
        setUnmatchedDrivers([...unmatched]);
        setFileUploaded(true);
      } catch (err) {
        console.error("XLS parse error:", err);
        alert("Error reading file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const openDriver = (driverName, force = false) => {
    const loads = uploadedGroups[driverName] || [];
    const matched = matchDriver(driverName, drivers);
    if (!force && matched && duplicateMap[matched.id]) {
      setDupModal({ driverName, existingStmt: duplicateMap[matched.id] });
      return;
    }
    setDupModal(null);
    setActiveDriverName(driverName);
    setActiveDriver(matched);
    setActiveLoads(loads);
    setDeductions({ ...DEFAULT_DEDUCTIONS });
    setLiveNetFuel(0);
    setView("generate");
  };

  // ── Helper: fetch toll transactions from latest import for a driver ──
  // ── Helper: fetch toll transactions from latest import for a driver ──
  const fetchTollTx = async (driverId) => {
    // First get the driver's pp_device_id
    const { data: driverRow } = await supabase
      .from("drivers")
      .select("pp_device_id")
      .eq("id", driverId)
      .single();
    const ppId = driverRow?.pp_device_id;
    if (!ppId) return [];

    // Find latest toll import for this pp_device_id
    const { data: importRows } = await supabase
      .from("fuel_toll_imports")
      .select("id")
      .eq("type", "toll")
      .eq("pp_device_id", ppId)
      .order("created_at", { ascending: false })
      .limit(1);
    const latestId = importRows?.[0]?.id;
    if (!latestId) return [];

    // Fetch transactions for that import
    const { data } = await supabase
      .from("toll_transactions")
      .select("pp_device_id, agency, exit_plaza, exit_date, exit_time, toll")
      .eq("import_id", latestId)
      .order("exit_date");
    return data || [];
  };
  const fetchFuelTx = async (driverId, pStart, pEnd) => {
    const { data: importRows } = await supabase
      .from("fuel_toll_imports")
      .select("id")
      .eq("type", "fuel")
      .eq("driver_id", driverId)
      .eq("period_start", pStart)
      .eq("period_end", pEnd);
    const ids = (importRows || []).map((r) => r.id);
    if (ids.length === 0) return [];
    const { data } = await supabase
      .from("fuel_transactions")
      .select(
        "transaction_date, amount, discount, merchant_city, merchant_state"
      )
      .in("import_id", ids)
      .order("transaction_date");
    return data || [];
  };

  // ── Initialize bulk deductions table ──
  const openBulkGenerate = async () => {
    if (!periodStart || !periodEnd) {
      alert("Please set period start and end first");
      return;
    }
    const { data: fuelImports } = await supabase
      .from("fuel_toll_imports")
      .select("driver_id, fuel_gross, fuel_discount")
      .eq("type", "fuel")
      .eq("period_start", periodStart)
      .eq("period_end", periodEnd);
    const { data: tollImports } = await supabase
      .from("fuel_toll_imports")
      .select("driver_id, toll")
      .eq("type", "toll")
      .gte("period_start", periodStart)
      .lte("period_end", periodEnd);
    const fuelMap = {};
    (fuelImports || []).forEach((r) => {
      fuelMap[r.driver_id] = r;
    });
    const tollMap = {};
    (tollImports || []).forEach((r) => {
      tollMap[r.driver_id] = r;
    });
    const init = {};
    Object.entries(uploadedGroups).forEach(([name]) => {
      const matched = matchDriver(name, drivers);
      if (matched) {
        const fuel = fuelMap[matched.id];

        init[name] = {
          ifta_eld: DEFAULT_DEDUCTIONS.ifta_eld,
          insurance: DEFAULT_DEDUCTIONS.insurance,
          samsara: DEFAULT_DEDUCTIONS.samsara,
          safety: DEFAULT_DEDUCTIONS.safety,
          fuel_gross: fuel
            ? parseFloat(parseFloat(fuel.fuel_gross || 0).toFixed(2))
            : 0,
          fuel_discount: fuel
            ? parseFloat(parseFloat(fuel.fuel_discount || 0).toFixed(2))
            : 0,
          toll: 0,
          other_amount: 0,
          other_description: "",
          reimbursement_amount: 0,
          reimbursement_description: "",
        };
      }
    });
    setBulkDeds(init);
    setBulkProgress([]);
    setBulkDone(false);
    setView("bulk");
  };

  // ── Generate one driver's statement (used by both single and bulk) ──
  const generateOneStatement = async ({
    driverObj,
    loads,
    deds,
    pStart,
    pEnd,
    sDate,
  }) => {
    // ── Pull fuel & toll from fuel_toll_imports for this driver + period ──
    const { data: fuelData } = await supabase
      .from("fuel_toll_imports")
      .select("fuel_gross, fuel_discount")
      .eq("type", "fuel")
      .eq("driver_id", driverObj.id)
      .eq("period_start", pStart)
      .eq("period_end", pEnd)
      .limit(1);

    const fuelImport = fuelData?.[0] || null;

    if (fuelImport) {
      deds = {
        ...deds,
        fuel_gross: parseFloat(fuelImport.fuel_gross || 0),
        fuel_discount: parseFloat(fuelImport.fuel_discount || 0),
      };
    }

    const tGross = loads.reduce((s, l) => s + l.gross, 0);
    const tPorsoFee = loads.reduce((s, l) => s + l.porso_fee, 0);
    const tDriverNet = loads.reduce((s, l) => s + l.driver_net, 0);
    const netFuel = calcNetFuel(deds);
    const totalDeds =
      parseFloat(deds.ifta_eld || 0) +
      parseFloat(deds.insurance || 0) +
      parseFloat(deds.samsara || 0) +
      parseFloat(deds.safety || 0) +
      parseFloat(deds.toll || 0) +
      netFuel +
      parseFloat(deds.other_amount || 0);
    const totalReimb = parseFloat(deds.reimbursement_amount || 0);
    const net = tDriverNet - totalDeds + totalReimb;

    const { data: stmtData, error: stmtError } = await supabase
      .from("statements")
      .insert({
        driver_id: driverObj.id,
        period_start: pStart,
        period_end: pEnd,
        statement_date: sDate,
        gross_earnings: tGross,
        porso_fee: tPorsoFee,
        total_deductions: totalDeds,
        reimbursements: totalReimb,
        net_pay: net,
      })
      .select()
      .single();
    if (stmtError) {
      console.error("Statement save error:", stmtError);
      return null;
    }

    await supabase.from("loads").insert(
      loads.map((l) => ({
        driver_id: driverObj.id,
        statement_id: stmtData.id,
        trip_id: l.trip_id,
        pickup: l.pickup,
        dropoff: l.dropoff,
        miles: l.miles,
        gross: l.gross,
        porso_fee: l.porso_fee,
        driver_net: l.driver_net,
        week_ending: pEnd,
      }))
    );
    await supabase.from("deductions").insert({
      driver_id: driverObj.id,
      statement_id: stmtData.id,
      week_ending: pEnd,
      ...deds,
    });

    const fuelTxData = await fetchFuelTx(driverObj.id, pStart, pEnd);
    const tollTxData = deds.toll > 0 ? await fetchTollTx(driverObj.id) : [];

    const driverName = `${driverObj.first_name} ${driverObj.last_name}`;
    const payTo = driverObj.corp_name || driverName;
    const doc = buildPDF({
      driverName,
      payTo,
      loads,
      deds,
      pStart,
      pEnd,
      sDate,
      totalDeds,
      net,
      fuelTransactions: fuelTxData,
      tollTransactions: tollTxData,
      logoImg,
    });

    if (driverObj.chat_id) {
      try {
        const pdfBuffer = doc.output("arraybuffer");
        const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
        const fileName = `${driverObj.id}_${pEnd}_${Date.now()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from("statements")
          .upload(fileName, pdfBlob, {
            contentType: "application/pdf",
            upsert: true,
          });
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from("statements")
            .getPublicUrl(fileName);
          await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              chat_id: driverObj.chat_id,
              text: `📊 *Settlement Statement*\n\nHi ${
                driverObj.first_name
              }! Your settlement statement for *${fmtDate(pStart)} – ${fmtDate(
                pEnd
              )}* is ready.\n\n💰 *Net Pay: ${fmt(
                net
              )}*\n\n📄 [Download your statement](${
                urlData.publicUrl
              })\n\n_Porso Freight Team 🚛_`,
              parse_mode: "Markdown",
            }),
          });
        }
      } catch (e) {
        console.error("Telegram send error:", e);
      }
    }
    return { net, hasTelegram: !!driverObj.chat_id };
  };

  // ── Bulk generate all ──
  const runBulkGenerate = async () => {
    setBulkGenerating(true);
    const matchedDrivers = Object.entries(uploadedGroups).filter(([name]) =>
      matchDriver(name, drivers)
    );
    const progress = matchedDrivers.map(([name]) => ({
      name,
      status: "waiting",
      netPay: 0,
      hasTelegram: false,
    }));
    setBulkProgress([...progress]);

    for (let i = 0; i < matchedDrivers.length; i++) {
      const [driverName, loads] = matchedDrivers[i];
      const driverObj = matchDriver(driverName, drivers);
      progress[i].status = "generating";
      setBulkProgress([...progress]);

      const driverDeds = {
        ...DEFAULT_DEDUCTIONS,
        ...(bulkDeds[driverName] || {}),
      };
      const result = await generateOneStatement({
        driverObj,
        loads,
        deds: driverDeds,
        pStart: periodStart,
        pEnd: periodEnd,
        sDate: statementDate,
      });

      if (result) {
        progress[i].status = "done";
        progress[i].netPay = result.net;
        progress[i].hasTelegram = result.hasTelegram;
      } else {
        progress[i].status = "error";
      }
      setBulkProgress([...progress]);
    }

    fetchStatements();
    setBulkGenerating(false);
    setBulkDone(true);
  };

  // ── Bulk deduction totals for summary bar ──
  const bulkMatchedEntries = Object.entries(uploadedGroups).filter(([name]) =>
    matchDriver(name, drivers)
  );
  const bulkTotalGross = bulkMatchedEntries.reduce(
    (s, [, loads]) => s + loads.reduce((a, l) => a + l.gross, 0),
    0
  );
  const bulkTotalNet = bulkMatchedEntries.reduce((s, [name, loads]) => {
    const driverNet = loads.reduce((a, l) => a + l.driver_net, 0);
    const deds = { ...DEFAULT_DEDUCTIONS, ...(bulkDeds[name] || {}) };
    const netFuel = calcNetFuel(deds);
    const totalDeds =
      parseFloat(deds.ifta_eld || 0) +
      parseFloat(deds.insurance || 0) +
      parseFloat(deds.samsara || 0) +
      parseFloat(deds.safety || 0) +
      parseFloat(deds.toll || 0) +
      netFuel +
      parseFloat(deds.other_amount || 0);
    const totalReimb = parseFloat(deds.reimbursement_amount || 0);
    return s + (driverNet - totalDeds + totalReimb);
  }, 0);
  const bulkTotalDeds = bulkTotalGross * (1 - PORSO_FEE_RATE) - bulkTotalNet;

  const totalGross = activeLoads.reduce((s, l) => s + l.gross, 0);
  const totalPorsoFee = activeLoads.reduce((s, l) => s + l.porso_fee, 0);
  const totalDriverNet = activeLoads.reduce((s, l) => s + l.driver_net, 0);
  const totalFixedDeductions =
    parseFloat(deductions.ifta_eld || 0) +
    parseFloat(deductions.insurance || 0) +
    parseFloat(deductions.samsara || 0) +
    parseFloat(deductions.safety || 0) +
    parseFloat(deductions.toll || 0) +
    liveNetFuel +
    parseFloat(deductions.other_amount || 0);
  const totalReimbursements = parseFloat(deductions.reimbursement_amount || 0);
  const netPay = totalDriverNet - totalFixedDeductions + totalReimbursements;

  const saveStatement = async () => {
    if (!activeDriver) return null;

    // ── Pull fuel & toll from fuel_toll_imports ──
    const { data: fuelData2 } = await supabase
      .from("fuel_toll_imports")
      .select("fuel_gross, fuel_discount")
      .eq("type", "fuel")
      .eq("driver_id", activeDriver.id)
      .eq("period_start", periodStart)
      .eq("period_end", periodEnd)
      .limit(1);

    const fuelImport2 = fuelData2?.[0] || null;

    if (fuelImport2) {
      deductions.fuel_gross = parseFloat(fuelImport2.fuel_gross || 0);
      deductions.fuel_discount = parseFloat(fuelImport2.fuel_discount || 0);
    }

    const { data: stmtData, error: stmtError } = await supabase
      .from("statements")
      .insert({
        driver_id: activeDriver.id,
        period_start: periodStart,
        period_end: periodEnd,
        statement_date: statementDate,
        gross_earnings: totalGross,
        porso_fee: totalPorsoFee,
        total_deductions: totalFixedDeductions,
        reimbursements: totalReimbursements,
        net_pay: netPay,
      })
      .select()
      .single();
    if (stmtError) {
      console.error("Statement save error:", stmtError);
      return null;
    }
    const statementId = stmtData.id;
    await supabase.from("loads").insert(
      activeLoads.map((l) => ({
        driver_id: activeDriver.id,
        statement_id: statementId,
        trip_id: l.trip_id,
        pickup: l.pickup,
        dropoff: l.dropoff,
        miles: l.miles,
        gross: l.gross,
        porso_fee: l.porso_fee,
        driver_net: l.driver_net,
        week_ending: periodEnd,
      }))
    );
    await supabase.from("deductions").insert({
      driver_id: activeDriver.id,
      statement_id: statementId,
      week_ending: periodEnd,
      ...deductions,
    });
    fetchStatements();
    return statementId;
  };

  const buildPDF = ({
    driverName,
    payTo,
    loads,
    deds,
    pStart,
    pEnd,
    sDate,
    totalDeds,
    net,
    fuelTransactions,
    tollTransactions,
  }) => {
    const doc = new jsPDF({ compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 16;
    const contentWidth = pageWidth - margin * 2;
    const ROW_H = 9;
    const BAR_H = 13;
    let y = 14;
    if (logoImg)
      doc.addImage(logoImg, "JPEG", margin, y, 50, 17, undefined, "MEDIUM");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text("SETTLEMENT STATEMENT", pageWidth - margin, y + 6, {
      align: "right",
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Statement Date: ${fmtDate(sDate)}`, pageWidth - margin, y + 13, {
      align: "right",
    });
    doc.text(
      `Period: ${fmtDate(pStart)} – ${fmtDate(pEnd)}`,
      pageWidth - margin,
      y + 19,
      { align: "right" }
    );
    y += 30;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("DRIVER", margin, y);
    doc.text("PAY TO", margin + contentWidth / 2, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(driverName, margin, y);
    doc.text(payTo, margin + contentWidth / 2, y);
    y += 13;
    const drawSectionHeader = (label) => {
      doc.setFillColor(58, 158, 95);
      doc.rect(margin, y, contentWidth, BAR_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(label, pageWidth / 2, y + BAR_H / 2 + 1.8, { align: "center" });
      y += BAR_H;
    };
    drawSectionHeader("EARNINGS");
    y += 1;
    doc.setFillColor(248, 248, 248);
    doc.rect(margin, y, contentWidth, ROW_H, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(130, 130, 130);
    const c = {
      tripId: margin + 2,
      pickup: margin + 30,
      dropoff: margin + 70,
      miles: margin + 108,
      gross: margin + 124,
      porso: margin + 143,
    };
    const colY = y + ROW_H / 2 + 3;
    doc.text("Trip ID", c.tripId, colY);
    doc.text("Pickup", c.pickup, colY);
    doc.text("Dropoff", c.dropoff, colY);
    doc.text("Miles", c.miles, colY);
    doc.text("Gross", c.gross, colY);
    doc.text("Rate", c.porso, colY);
    doc.text("Driver net", pageWidth - margin - 4, colY, { align: "right" });
    y += ROW_H;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const tGross = loads.reduce((s, l) => s + parseFloat(l.gross || 0), 0);
    const tNet = loads.reduce((s, l) => s + parseFloat(l.driver_net || 0), 0);
    loads.forEach((load, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, y, contentWidth, ROW_H, "F");
      }
      const rowTextY = y + ROW_H / 2 + 3;
      doc.setTextColor(40, 40, 40);
      doc.text(String(load.trip_id || "—"), c.tripId, rowTextY);
      doc.text((load.pickup || "—").slice(0, 14), c.pickup, rowTextY);
      doc.text((load.dropoff || "—").slice(0, 14), c.dropoff, rowTextY);
      doc.text(String(load.miles || 0), c.miles, rowTextY);
      doc.text(fmt(load.gross), c.gross, rowTextY);
      doc.setTextColor(100, 100, 100);
      doc.text("90%", c.porso, rowTextY);
      doc.setTextColor(58, 109, 17);
      doc.text(fmt(load.driver_net), pageWidth - margin - 4, rowTextY, {
        align: "right",
      });
      y += ROW_H;
    });
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    doc.text("Total Gross:", c.gross - 24, y);
    doc.text(fmt(tGross), c.gross, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(58, 109, 17);
    doc.text(fmt(tNet), pageWidth - margin - 4, y, { align: "right" });
    y += 14;
    drawSectionHeader("DEDUCTIONS");
    y += 1;
    const standardRows = [
      ["IFTA / ELD", deds?.ifta_eld || 0],
      ["Insurance", deds?.insurance || 0],
      ["Samsara", deds?.samsara || 0],
      ["Safety", deds?.safety || 0],
      (deds?.toll || 0) > 0 ? ["Toll", deds.toll] : null,
    ].filter(Boolean);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    let rowIndex = 0;
    standardRows.forEach(([label, amount]) => {
      if (rowIndex % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, y, contentWidth, ROW_H, "F");
      }
      const rowTextY = y + ROW_H / 2 + 3;
      doc.setTextColor(60, 60, 60);
      doc.text(label, margin + 4, rowTextY);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(amount)}`, pageWidth - margin - 4, rowTextY, {
        align: "right",
      });
      y += ROW_H;
      rowIndex++;
    });
    const fuelGross = parseFloat(deds?.fuel_gross || 0);
    const fuelDiscount = parseFloat(deds?.fuel_discount || 0);
    const netFuelAmt = Math.max(0, fuelGross - fuelDiscount);
    if (fuelGross > 0) {
      if (rowIndex % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, y, contentWidth, ROW_H, "F");
      }
      let rowTextY = y + ROW_H / 2 + 3;
      doc.setTextColor(60, 60, 60);
      doc.text("Fuel Gross", margin + 4, rowTextY);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(fuelGross)}`, pageWidth - margin - 4, rowTextY, {
        align: "right",
      });
      y += ROW_H;
      rowIndex++;
      if (fuelDiscount > 0) {
        doc.setFillColor(250, 238, 218);
        doc.rect(margin, y, contentWidth, ROW_H, "F");
        rowTextY = y + ROW_H / 2 + 3;
        doc.setTextColor(133, 79, 11);
        doc.text("—", margin + 4, rowTextY);
        doc.setTextColor(100, 100, 100);
        doc.text("Fuel Discount", margin + 12, rowTextY);
        doc.setTextColor(58, 109, 17);
        doc.text(`+${fmt(fuelDiscount)}`, pageWidth - margin - 4, rowTextY, {
          align: "right",
        });
        y += ROW_H;
        rowIndex++;
      }
      if (rowIndex % 2 === 0) {
        doc.setFillColor(248, 248, 248);
      } else {
        doc.setFillColor(245, 245, 245);
      }
      doc.rect(margin, y, contentWidth, ROW_H, "F");
      rowTextY = y + ROW_H / 2 + 3;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text("Net Fuel", margin + 4, rowTextY);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(netFuelAmt)}`, pageWidth - margin - 4, rowTextY, {
        align: "right",
      });
      doc.setFont("helvetica", "normal");
      y += ROW_H;
      rowIndex++;
    }
    if ((deds?.other_amount || 0) > 0) {
      if (rowIndex % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, y, contentWidth, ROW_H, "F");
      }
      const rowTextY = y + ROW_H / 2 + 3;
      doc.setTextColor(60, 60, 60);
      doc.text(deds?.other_description || "Other", margin + 4, rowTextY);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(deds.other_amount)}`, pageWidth - margin - 4, rowTextY, {
        align: "right",
      });
      y += ROW_H;
    }
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text("Total Deductions:", margin + 4, y);
    doc.setTextColor(163, 45, 45);
    doc.text(`-${fmt(totalDeds)}`, pageWidth - margin - 4, y, {
      align: "right",
    });
    y += 14;
    const reimbAmt = parseFloat(deds?.reimbursement_amount || 0);
    if (reimbAmt > 0) {
      drawSectionHeader("REIMBURSEMENTS");
      y += 1;
      doc.setFillColor(252, 252, 252);
      doc.rect(margin, y, contentWidth, ROW_H, "F");
      const reimRowY = y + ROW_H / 2 + 3;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(
        deds?.reimbursement_description || "Reimbursement",
        margin + 4,
        reimRowY
      );
      doc.setTextColor(58, 109, 17);
      doc.text(`+${fmt(reimbAmt)}`, pageWidth - margin - 4, reimRowY, {
        align: "right",
      });
      y += ROW_H + 10;
    }
    const netBarH = 22;
    doc.setFillColor(26, 30, 28);
    doc.rect(margin, y, contentWidth, netBarH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text("NET PAY", pageWidth / 2, y + netBarH / 2 - 1.5, {
      align: "center",
    });
    doc.setFontSize(18);
    doc.setTextColor(61, 204, 130);
    doc.text(fmt(net), pageWidth / 2, y + netBarH / 2 + 7, { align: "center" });
    y += netBarH + 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 180);
    doc.text(
      "Porso Freight LLC · 121 Milton St, Dayton OH 45403",
      pageWidth / 2,
      y,
      { align: "center" }
    );

    // ── FUEL TRANSACTION DETAIL SECTION ──
    if (fuelTransactions && fuelTransactions.length > 0) {
      doc.addPage();
      y = 14;
      doc.setFillColor(58, 158, 95);
      doc.rect(margin, y, contentWidth, BAR_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text("FUEL TRANSACTION DETAIL", pageWidth / 2, y + BAR_H / 2 + 1.8, {
        align: "center",
      });
      y += BAR_H + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        "A breakdown of all fuel transactions for this settlement period.",
        margin,
        y
      );
      y += 10;
      const tc = {
        date: margin + 2,
        city: margin + 40,
        state: margin + 100,
        amount: margin + 118,
        discount: margin + 150,
      };
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, y, contentWidth, ROW_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      const txColY = y + ROW_H / 2 + 3;
      doc.text("DATE", tc.date, txColY);
      doc.text("CITY", tc.city, txColY);
      doc.text("ST", tc.state, txColY);
      doc.text("AMOUNT", tc.amount, txColY);
      doc.text("DISCOUNT", tc.discount, txColY);
      y += ROW_H;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      fuelTransactions.forEach((tx, i) => {
        if (y > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          y = 14;
        }
        if (i % 2 === 0) {
          doc.setFillColor(252, 252, 252);
          doc.rect(margin, y, contentWidth, ROW_H, "F");
        }
        const txRowY = y + ROW_H / 2 + 3;
        doc.setTextColor(60, 60, 60);
        doc.text(
          String(tx.transaction_date || "—").slice(0, 18),
          tc.date,
          txRowY
        );
        doc.text(String(tx.merchant_city || "—").slice(0, 16), tc.city, txRowY);
        doc.text(String(tx.merchant_state || "—"), tc.state, txRowY);
        doc.setTextColor(40, 40, 40);
        doc.text(fmt(tx.amount), tc.amount, txRowY);
        doc.setTextColor(58, 109, 17);
        doc.text(
          tx.discount > 0 ? `-${fmt(tx.discount)}` : "—",
          tc.discount,
          txRowY
        );
        y += ROW_H;
      });
      const txTotalGross = fuelTransactions.reduce(
        (s, t) => s + parseFloat(t.amount || 0),
        0
      );
      const txTotalDiscount = fuelTransactions.reduce(
        (s, t) => s + parseFloat(t.discount || 0),
        0
      );
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);
      doc.text("Total", tc.date, y);
      doc.text(fmt(txTotalGross), tc.amount, y);
      doc.setTextColor(58, 109, 17);
      doc.text(`-${fmt(txTotalDiscount)}`, tc.discount, y);
      y += 10;
      const netFuelTx = Math.max(0, txTotalGross - txTotalDiscount);
      doc.setFillColor(234, 243, 222);
      doc.rect(margin, y, contentWidth, 14, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(58, 109, 17);
      doc.text("Net fuel deduction", margin + 4, y + 9);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(netFuelTx)}`, pageWidth - margin - 4, y + 9, {
        align: "right",
      });
      y += 24;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(180, 180, 180);
      doc.text(
        "Porso Freight LLC · 121 Milton St, Dayton OH 45403",
        pageWidth / 2,
        y,
        { align: "center" }
      );
    }

    // ── TOLL TRANSACTION DETAIL SECTION ──
    if (tollTransactions && tollTransactions.length > 0) {
      // Only add new page if not enough space, otherwise continue on same page
      if (!fuelTransactions || fuelTransactions.length === 0) {
        doc.addPage();
        y = 14;
      } else {
        y += 16;
      }
      doc.setFillColor(58, 158, 95);
      doc.rect(margin, y, contentWidth, BAR_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text("TOLL TRANSACTION DETAIL", pageWidth / 2, y + BAR_H / 2 + 1.8, {
        align: "center",
      });
      y += BAR_H + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        "A breakdown of all toll transactions for this settlement period.",
        margin,
        y
      );
      y += 10;
      const ttc = {
        ppId: margin + 2,
        agency: margin + 28,
        plaza: margin + 70,
        date: margin + 122,
        time: margin + 148,
        toll: margin + 168,
      };
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, y, contentWidth, ROW_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      const ttColY = y + ROW_H / 2 + 3;
      doc.text("PP DEVICE", ttc.ppId, ttColY);
      doc.text("AGENCY", ttc.agency, ttColY);
      doc.text("EXIT PLAZA", ttc.plaza, ttColY);
      doc.text("DATE", ttc.date, ttColY);
      doc.text("TIME", ttc.time, ttColY);
      doc.text("TOLL", ttc.toll, ttColY);
      y += ROW_H;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      tollTransactions.forEach((tx, i) => {
        if (y > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          y = 14;
        }
        if (i % 2 === 0) {
          doc.setFillColor(252, 252, 252);
          doc.rect(margin, y, contentWidth, ROW_H, "F");
        }
        const ttRowY = y + ROW_H / 2 + 3;
        doc.setTextColor(60, 60, 60);
        doc.text(String(tx.pp_device_id || "—").slice(0, 10), ttc.ppId, ttRowY);
        doc.text(String(tx.agency || "—").slice(0, 16), ttc.agency, ttRowY);
        doc.text(String(tx.exit_plaza || "—").slice(0, 18), ttc.plaza, ttRowY);
        doc.text(String(tx.exit_date || "—").slice(0, 10), ttc.date, ttRowY);
        doc.text(String(tx.exit_time || "—").slice(0, 8), ttc.time, ttRowY);
        doc.setTextColor(163, 45, 45);
        doc.text(fmt(tx.toll), ttc.toll, ttRowY);
        y += ROW_H;
      });
      const ttTotal = tollTransactions.reduce(
        (s, t) => s + parseFloat(t.toll || 0),
        0
      );
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);
      doc.text("Total", ttc.ppId, y);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(ttTotal)}`, pageWidth - margin - 4, y, {
        align: "right",
      });
      y += 10;
      doc.setFillColor(234, 243, 222);
      doc.rect(margin, y, contentWidth, 14, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(58, 109, 17);
      doc.text("Net toll deduction", margin + 4, y + 9);
      doc.setTextColor(163, 45, 45);
      doc.text(`-${fmt(ttTotal)}`, pageWidth - margin - 4, y + 9, {
        align: "right",
      });
      y += 24;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(180, 180, 180);
      doc.text(
        "Porso Freight LLC · 121 Milton St, Dayton OH 45403",
        pageWidth / 2,
        y,
        { align: "center" }
      );
    }

    return doc;
  };

  const generatePDF = async () => {
    setGenerating(true);
    await saveStatement();
    const singleFuelTx = await fetchFuelTx(
      activeDriver?.id,
      periodStart,
      periodEnd
    );
    const singleTollTx =
      deductions.toll > 0 ? await fetchTollTx(activeDriver?.id) : [];
    const doc = buildPDF({
      driverName: activeDriverName,
      payTo: activeDriver?.corp_name || activeDriverName,
      loads: activeLoads,
      deds: deductions,
      pStart: periodStart,
      pEnd: periodEnd,
      sDate: statementDate,
      totalDeds: totalFixedDeductions,
      net: netPay,
      fuelTransactions: singleFuelTx,
      tollTransactions: singleTollTx,
    });
    window.open(URL.createObjectURL(doc.output("blob")), "_blank");
    if (activeDriver?.chat_id) {
      try {
        const pdfBuffer = doc.output("arraybuffer");
        const pdfUploadBlob = new Blob([pdfBuffer], {
          type: "application/pdf",
        });
        const fileName = `${activeDriver.id}_${periodEnd}_${Date.now()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from("statements")
          .upload(fileName, pdfUploadBlob, {
            contentType: "application/pdf",
            upsert: true,
          });
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from("statements")
            .getPublicUrl(fileName);
          await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              chat_id: activeDriver.chat_id,
              text: `📊 *Settlement Statement*\n\nHi ${
                activeDriver.first_name
              }! Your settlement statement for *${fmtDate(
                periodStart
              )} – ${fmtDate(periodEnd)}* is ready.\n\n💰 *Net Pay: ${fmt(
                netPay
              )}*\n\n📄 [Download your statement](${
                urlData.publicUrl
              })\n\n_Porso Freight Team 🚛_`,
              parse_mode: "Markdown",
            }),
          });
        }
      } catch (e) {
        console.error("Telegram send error:", e);
      }
    }
    setGenerating(false);
  };

  const downloadPDF = async () => {
    if (!detailStatement || !detailDeductions) return;

    const dName = `${detailStatement.drivers?.first_name} ${detailStatement.drivers?.last_name}`;
    const netFuel = calcNetFuel(detailDeductions);
    const recalcDeds =
      parseFloat(detailDeductions.ifta_eld || 0) +
      parseFloat(detailDeductions.insurance || 0) +
      parseFloat(detailDeductions.samsara || 0) +
      parseFloat(detailDeductions.safety || 0) +
      parseFloat(detailDeductions.toll || 0) +
      netFuel +
      parseFloat(detailDeductions.other_amount || 0);
    const recalcReimb = parseFloat(detailDeductions.reimbursement_amount || 0);
    const recalcNet =
      detailLoads.reduce((s, l) => s + parseFloat(l.driver_net || 0), 0) -
      recalcDeds +
      recalcReimb;
    const dlFuelTx = await fetchFuelTx(
      detailStatement.driver_id,
      detailStatement.period_start,
      detailStatement.period_end
    );
    const dlTollTx =
      detailDeductions?.toll > 0
        ? await fetchTollTx(detailStatement.driver_id)
        : [];
    const doc = buildPDF({
      driverName: dName,
      payTo: detailStatement.drivers?.corp_name || dName,
      loads: detailLoads,
      deds: detailDeductions,
      pStart: detailStatement.period_start,
      pEnd: detailStatement.period_end,
      sDate: detailStatement.statement_date,
      totalDeds: recalcDeds,
      net: recalcNet,
      fuelTransactions: dlFuelTx,
      tollTransactions: dlTollTx,
    });
    window.open(URL.createObjectURL(doc.output("blob")), "_blank");
  };

  const btnStyle = (bg, color, disabled = false) => ({
    background: bg,
    color,
    fontSize: "13px",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    padding: "8px 18px",
    borderRadius: "8px",
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    fontFamily: "inherit",
  });

  const dedInput = (value, key, setter) => (
    <NumInput
      value={value}
      onChange={(v) => setter((p) => ({ ...p, [key]: v }))}
    />
  );

  const backToList = () => {
    setView("list");
    setFileUploaded(false);
    setUploadedGroups({});
    setActiveDriverName(null);
    setDetailStatement(null);
    setEditMode(false);
    setBulkDeds({});
    setBulkProgress([]);
    setBulkDone(false);
  };

  // ── Bulk deductions table mini input ──
  const updateBulkDed = (driverName, key, value) => {
    setBulkDeds((prev) => ({
      ...prev,
      [driverName]: { ...(prev[driverName] || {}), [key]: value },
    }));
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile && <MobileHeader title="Settlements" />}
        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="t-page-title">Settlements</div>
          <div style={{ display: "flex", gap: "8px" }}>
            {view !== "list" && (
              <button onClick={backToList} style={btnStyle("#f0f0f0", "#555")}>
                ← Back
              </button>
            )}
            {view === "list" && (
              <button
                onClick={() => setView("new")}
                style={btnStyle("#1a1e1c", "#fff")}
              >
                + New settlement
              </button>
            )}
          </div>
        </div>

        {dupModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setDupModal(null);
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                maxWidth: "400px",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#faeeda",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: "18px",
                  }}
                >
                  ⚠️
                </div>
                <div>
                  <div
                    className="t-name"
                    style={{ fontSize: "14px", marginBottom: "4px" }}
                  >
                    Statement already exists
                  </div>
                  <div
                    className="t-body"
                    style={{ color: "#6b7280", fontSize: "12px" }}
                  >
                    <strong>{dupModal.driverName}</strong> already has a
                    statement for{" "}
                    <strong>
                      {fmtDate(periodStart, { month: "short", day: "numeric" })}{" "}
                      –{" "}
                      {fmtDate(periodEnd, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </strong>
                    . Generating again will create a duplicate.
                  </div>
                </div>
              </div>
              <div
                style={{
                  background: "#fafafa",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                  }}
                >
                  <span className="t-label" style={{ color: "#6b7280" }}>
                    Existing net pay
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#3b6d11",
                    }}
                  >
                    {fmt(dupModal.existingStmt.net_pay)}
                  </span>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span className="t-label" style={{ color: "#6b7280" }}>
                    Generated on
                  </span>
                  <span className="t-data" style={{ color: "#6b7280" }}>
                    {fmtDate(dupModal.existingStmt.statement_date)}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setDupModal(null)}
                  style={{
                    ...btnStyle("#f0f0f0", "#555"),
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => openDriver(dupModal.driverName, true)}
                  style={{
                    background: "#faeeda",
                    color: "#633806",
                    fontSize: "13px",
                    fontWeight: "500",
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "0.5px solid #FAC775",
                    cursor: "pointer",
                    flex: 1,
                    textAlign: "center",
                    fontFamily: "inherit",
                  }}
                >
                  Generate anyway
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            padding: isMobile ? "12px" : "24px",
            maxWidth: view === "bulk" ? "1280px" : "960px",
          }}
        >
          {/* LIST */}
          {view === "list" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {loadingStatements ? (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "40px",
                    textAlign: "center",
                  }}
                  className="t-body"
                >
                  Loading...
                </div>
              ) : statements.length === 0 ? (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "40px",
                    textAlign: "center",
                  }}
                  className="t-body"
                >
                  No statements yet — click New settlement to get started
                </div>
              ) : (
                periodGroups.map((group) => {
                  const key = `${group.period_start}|${group.period_end}`;
                  const isCollapsed = collapsedPeriods[key];
                  const periodLabel = `${fmtDate(group.period_start, {
                    month: "short",
                    day: "numeric",
                  })} – ${fmtDate(group.period_end, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}`;
                  return (
                    <div
                      key={key}
                      style={{
                        background: "#fff",
                        border: "0.5px solid #e8e8e8",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        onClick={() => togglePeriod(key)}
                        style={{
                          padding: "14px 20px",
                          background: "#fafafa",
                          borderBottom: isCollapsed
                            ? "none"
                            : "0.5px solid #e8e8e8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f3f3f3")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "#fafafa")
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#9ca3af"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              transform: isCollapsed
                                ? "rotate(-90deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.15s",
                              flexShrink: 0,
                            }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          <div>
                            <div className="t-name">{periodLabel}</div>
                            <div
                              className="t-label"
                              style={{ color: "#9ca3af", marginTop: "1px" }}
                            >
                              {group.statements.length}{" "}
                              {group.statements.length === 1
                                ? "driver"
                                : "drivers"}{" "}
                              · {fmt(group.totalGross)} gross
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#3b6d11",
                            background: "#eaf3de",
                            padding: "3px 10px",
                            borderRadius: "20px",
                            fontWeight: "500",
                          }}
                        >
                          {fmt(group.totalNetPay)} paid
                        </div>
                      </div>
                      {!isCollapsed && (
                        <>
                          {!isMobile && (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "2fr 1fr 1fr 1fr 60px",
                                padding: "8px 20px 8px 48px",
                                background: "#fafafa",
                                borderBottom: "0.5px solid #e8e8e8",
                              }}
                            >
                              {[
                                "Driver",
                                "Gross",
                                "Deductions",
                                "Net pay",
                                "",
                              ].map((h) => (
                                <div key={h} className="t-table-header">
                                  {h}
                                </div>
                              ))}
                            </div>
                          )}
                          {group.statements.map((s, i) => (
                            <div
                              key={s.id}
                              onClick={() => openDetail(s)}
                              style={{
                                display: isMobile ? "block" : "grid",
                                gridTemplateColumns: "2fr 1fr 1fr 1fr 60px",
                                padding: "11px 20px 11px 48px",
                                borderBottom:
                                  i < group.statements.length - 1
                                    ? "0.5px solid #f0f0f0"
                                    : "none",
                                alignItems: "center",
                                cursor: "pointer",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#fafafa")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "#fff")
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "50%",
                                    background: "#eaf3de",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    fontWeight: "500",
                                    color: "#3a9e5f",
                                    flexShrink: 0,
                                  }}
                                >
                                  {`${s.drivers?.first_name?.[0] || ""}${
                                    s.drivers?.last_name?.[0] || ""
                                  }`}
                                </div>
                                <div>
                                  <div className="t-name">
                                    {s.drivers?.first_name}{" "}
                                    {s.drivers?.last_name}
                                  </div>
                                  {s.drivers?.corp_name && (
                                    <div
                                      className="t-label"
                                      style={{ color: "#9ca3af" }}
                                    >
                                      {s.drivers.corp_name}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="t-data">
                                {fmt(s.gross_earnings)}
                              </div>
                              <div
                                className="t-data"
                                style={{ color: "#a32d2d" }}
                              >
                                -{fmt(s.total_deductions)}
                              </div>
                              <div
                                className="t-data"
                                style={{ color: "#3b6d11", fontWeight: "500" }}
                              >
                                {fmt(s.net_pay)}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#3a9e5f",
                                  fontWeight: "500",
                                }}
                              >
                                View →
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* DETAIL */}
          {view === "detail" && detailStatement && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  padding: "16px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "#eaf3de",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#3a9e5f",
                      flexShrink: 0,
                    }}
                  >
                    {`${detailStatement.drivers?.first_name?.[0] || ""}${
                      detailStatement.drivers?.last_name?.[0] || ""
                    }`}
                  </div>
                  <div>
                    <div className="t-name" style={{ fontSize: "14px" }}>
                      {detailStatement.drivers?.first_name}{" "}
                      {detailStatement.drivers?.last_name}
                    </div>
                    <div className="t-label" style={{ color: "#9ca3af" }}>
                      {detailStatement.drivers?.corp_name
                        ? `Pay to: ${detailStatement.drivers.corp_name}`
                        : "Pay to: driver name"}{" "}
                      · {fmtDate(detailStatement.period_start)} –{" "}
                      {fmtDate(detailStatement.period_end)}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  {!editMode && (
                    <>
                      <button
                        onClick={enterEditMode}
                        style={btnStyle("#f0f0f0", "#555")}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={downloadPDF}
                        style={btnStyle("#1a1e1c", "#fff")}
                      >
                        📄 Download PDF
                      </button>
                    </>
                  )}
                  {editMode && (
                    <>
                      <button
                        onClick={() => setEditMode(false)}
                        style={btnStyle("#f0f0f0", "#555")}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEdits(false)}
                        disabled={saving}
                        style={btnStyle("#f0f0f0", "#555", saving)}
                      >
                        {saving ? "Saving..." : "💾 Save"}
                      </button>
                      <button
                        onClick={() => saveEdits(true)}
                        disabled={saving}
                        style={btnStyle("#3a9e5f", "#fff", saving)}
                      >
                        {saving ? "Saving..." : "📄 Save & resend"}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {editMode && (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "20px",
                  }}
                >
                  <div className="t-name" style={{ marginBottom: "16px" }}>
                    Statement period
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <div className="t-label" style={{ marginBottom: "4px" }}>
                        Period start
                      </div>
                      <input
                        type="date"
                        value={editPeriodStart}
                        onChange={(e) => setEditPeriodStart(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <div className="t-label" style={{ marginBottom: "4px" }}>
                        Period end
                      </div>
                      <input
                        type="date"
                        value={editPeriodEnd}
                        onChange={(e) => setEditPeriodEnd(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <div className="t-label" style={{ marginBottom: "4px" }}>
                        Statement date
                      </div>
                      <input
                        type="date"
                        value={editStatementDate}
                        onChange={(e) => setEditStatementDate(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              )}
              {loadingDetail ? (
                <div
                  style={{ padding: "40px", textAlign: "center" }}
                  className="t-body"
                >
                  Loading statement details...
                </div>
              ) : (
                <>
                  <div
                    style={{
                      background: "#fff",
                      border: "0.5px solid #e8e8e8",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "14px 20px",
                        borderBottom: "0.5px solid #e8e8e8",
                        background: "#fafafa",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="t-table-header">
                        Trips ({detailLoads.length})
                      </div>
                      <div className="t-label" style={{ color: "#3a9e5f" }}>
                        Total gross: {fmt(detailStatement.gross_earnings)}
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "12px",
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#fafafa" }}>
                            {[
                              "Trip ID",
                              "Pickup",
                              "Dropoff",
                              "Miles",
                              "Gross",
                              "Rate",
                              "Driver net",
                            ].map((h) => (
                              <th
                                key={h}
                                className="t-table-header"
                                style={{
                                  padding: "9px 12px",
                                  textAlign:
                                    h === "Trip ID" ||
                                    h === "Pickup" ||
                                    h === "Dropoff"
                                      ? "left"
                                      : "right",
                                  fontWeight: "500",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detailLoads.map((load, i) => (
                            <tr
                              key={i}
                              style={{
                                borderTop: "0.5px solid #f0f0f0",
                                background: i % 2 === 1 ? "#fafafa" : "#fff",
                              }}
                            >
                              <td
                                className="t-data"
                                style={{ padding: "9px 12px" }}
                              >
                                {load.trip_id}
                              </td>
                              <td
                                className="t-body"
                                style={{
                                  padding: "9px 12px",
                                  color: "#6b7280",
                                }}
                              >
                                {load.pickup || "—"}
                              </td>
                              <td
                                className="t-body"
                                style={{
                                  padding: "9px 12px",
                                  color: "#6b7280",
                                }}
                              >
                                {load.dropoff || "—"}
                              </td>
                              <td
                                className="t-data"
                                style={{
                                  padding: "9px 12px",
                                  textAlign: "right",
                                }}
                              >
                                {load.miles}
                              </td>
                              <td
                                className="t-data"
                                style={{
                                  padding: "9px 12px",
                                  textAlign: "right",
                                }}
                              >
                                {fmt(load.gross)}
                              </td>
                              <td
                                className="t-data"
                                style={{
                                  padding: "9px 12px",
                                  textAlign: "right",
                                  color: "#6b7280",
                                }}
                              >
                                90%
                              </td>
                              <td
                                className="t-data"
                                style={{
                                  padding: "9px 12px",
                                  textAlign: "right",
                                  color: "#3b6d11",
                                  fontWeight: "500",
                                }}
                              >
                                {fmt(load.driver_net)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr
                            style={{
                              borderTop: "1px solid #e8e8e8",
                              background: "#fafafa",
                            }}
                          >
                            <td
                              colSpan={4}
                              className="t-label"
                              style={{ padding: "9px 12px" }}
                            >
                              Totals
                            </td>
                            <td
                              className="t-data"
                              style={{
                                padding: "9px 12px",
                                textAlign: "right",
                                fontWeight: "600",
                              }}
                            >
                              {fmt(
                                detailLoads.reduce(
                                  (s, l) => s + parseFloat(l.gross || 0),
                                  0
                                )
                              )}
                            </td>
                            <td style={{ padding: "9px 12px" }}></td>
                            <td
                              className="t-data"
                              style={{
                                padding: "9px 12px",
                                textAlign: "right",
                                color: "#3b6d11",
                                fontWeight: "600",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {fmt(
                                detailLoads.reduce(
                                  (s, l) => s + parseFloat(l.driver_net || 0),
                                  0
                                )
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "0.5px solid #e8e8e8",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <div className="t-name" style={{ marginBottom: "16px" }}>
                      Deductions
                    </div>
                    {editMode && editDeds ? (
                      <>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile
                              ? "1fr 1fr"
                              : "repeat(3, 1fr)",
                            gap: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          {[
                            { key: "ifta_eld", label: "IFTA / ELD" },
                            { key: "insurance", label: "Insurance" },
                            { key: "samsara", label: "Samsara" },
                            { key: "safety", label: "Safety" },
                            { key: "toll", label: "Toll" },
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <div
                                className="t-label"
                                style={{ marginBottom: "4px" }}
                              >
                                {label}
                              </div>
                              {dedInput(editDeds[key], key, setEditDeds)}
                            </div>
                          ))}
                        </div>
                        <FuelFields
                          deds={editDeds}
                          setDeds={setEditDeds}
                          netFuelVal={liveEditNetFuel}
                        />
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr",
                            gap: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          <div>
                            <div
                              className="t-label"
                              style={{ marginBottom: "4px" }}
                            >
                              Other deduction
                            </div>
                            <input
                              value={editDeds.other_description}
                              onChange={(e) =>
                                setEditDeds((p) => ({
                                  ...p,
                                  other_description: e.target.value,
                                }))
                              }
                              placeholder="e.g. Advance"
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <div
                              className="t-label"
                              style={{ marginBottom: "4px" }}
                            >
                              Amount
                            </div>
                            {dedInput(
                              editDeds.other_amount,
                              "other_amount",
                              setEditDeds
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr",
                            gap: "12px",
                          }}
                        >
                          <div>
                            <div
                              className="t-label"
                              style={{ marginBottom: "4px" }}
                            >
                              Reimbursement
                            </div>
                            <input
                              value={editDeds.reimbursement_description}
                              onChange={(e) =>
                                setEditDeds((p) => ({
                                  ...p,
                                  reimbursement_description: e.target.value,
                                }))
                              }
                              placeholder="e.g. Parking refund"
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <div
                              className="t-label"
                              style={{ marginBottom: "4px" }}
                            >
                              Amount
                            </div>
                            {dedInput(
                              editDeds.reimbursement_amount,
                              "reimbursement_amount",
                              setEditDeds
                            )}
                          </div>
                        </div>
                      </>
                    ) : detailDeductions ? (
                      <>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile
                              ? "1fr 1fr"
                              : "repeat(3, 1fr)",
                            gap: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          {[
                            { key: "ifta_eld", label: "IFTA / ELD" },
                            { key: "insurance", label: "Insurance" },
                            { key: "samsara", label: "Samsara" },
                            { key: "safety", label: "Safety" },
                            { key: "toll", label: "Toll" },
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <div
                                className="t-label"
                                style={{ marginBottom: "2px" }}
                              >
                                {label}
                              </div>
                              <div
                                className="t-data"
                                style={{
                                  color:
                                    detailDeductions[key] > 0
                                      ? "#a32d2d"
                                      : "#9ca3af",
                                }}
                              >
                                {detailDeductions[key] > 0
                                  ? `-${fmt(detailDeductions[key])}`
                                  : "—"}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginBottom: "12px" }}>
                          <FuelDisplay deds={detailDeductions} />
                        </div>
                        {detailDeductions.other_amount > 0 && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "2fr 1fr",
                              gap: "12px",
                              marginBottom: "12px",
                            }}
                          >
                            <div>
                              <div
                                className="t-label"
                                style={{ marginBottom: "2px" }}
                              >
                                Other
                              </div>
                              <div className="t-body" style={{ color: "#111" }}>
                                {detailDeductions.other_description || "Other"}
                              </div>
                            </div>
                            <div>
                              <div
                                className="t-label"
                                style={{ marginBottom: "2px" }}
                              >
                                Amount
                              </div>
                              <div
                                className="t-data"
                                style={{ color: "#a32d2d" }}
                              >
                                -{fmt(detailDeductions.other_amount)}
                              </div>
                            </div>
                          </div>
                        )}
                        {detailDeductions.reimbursement_amount > 0 && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "2fr 1fr",
                              gap: "12px",
                            }}
                          >
                            <div>
                              <div
                                className="t-label"
                                style={{ marginBottom: "2px" }}
                              >
                                Reimbursement
                              </div>
                              <div className="t-body" style={{ color: "#111" }}>
                                {detailDeductions.reimbursement_description ||
                                  "Reimbursement"}
                              </div>
                            </div>
                            <div>
                              <div
                                className="t-label"
                                style={{ marginBottom: "2px" }}
                              >
                                Amount
                              </div>
                              <div
                                className="t-data"
                                style={{ color: "#3b6d11" }}
                              >
                                +{fmt(detailDeductions.reimbursement_amount)}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                  <div
                    style={{
                      background: "#1a1e1c",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr 1fr"
                          : "repeat(4, 1fr)",
                        gap: "16px",
                      }}
                    >
                      {[
                        {
                          label: "Total gross",
                          value: fmt(
                            editMode
                              ? editTotalGross
                              : detailStatement.gross_earnings
                          ),
                          color: "#fff",
                        },
                        {
                          label: "Porso fee (10%)",
                          value: `-${fmt(
                            editMode
                              ? editTotalPorsoFee
                              : detailStatement.porso_fee
                          )}`,
                          color: "#f87171",
                        },
                        {
                          label: "Total deductions",
                          value: `-${fmt(
                            editMode
                              ? editTotalDeds
                              : detailStatement.total_deductions
                          )}`,
                          color: "#f87171",
                        },
                        {
                          label: "Net pay",
                          value: fmt(
                            editMode ? editNetPay : detailStatement.net_pay
                          ),
                          color: "#3dcc82",
                        },
                      ].map(({ label, value, color }) => (
                        <div key={label}>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "rgba(255,255,255,0.4)",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              marginBottom: "4px",
                            }}
                          >
                            {label}
                          </div>
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "600",
                              letterSpacing: "-0.02em",
                              color,
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* NEW — with period picker */}
          {view === "new" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  padding: "20px",
                }}
              >
                <div className="t-name" style={{ marginBottom: "4px" }}>
                  Set period
                </div>
                <div
                  className="t-label"
                  style={{ color: "#9ca3af", marginBottom: "16px" }}
                >
                  Pick an existing period or enter a new one below
                </div>
                {periodGroups.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      marginBottom: "16px",
                    }}
                  >
                    {periodGroups.slice(0, 6).map((g) => {
                      const pLabel = `${fmtDate(g.period_start, {
                        month: "short",
                        day: "numeric",
                      })} – ${fmtDate(g.period_end, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`;
                      const isSelected =
                        periodStart === g.period_start &&
                        periodEnd === g.period_end;
                      return (
                        <button
                          key={`${g.period_start}|${g.period_end}`}
                          onClick={() => {
                            setPeriodStart(g.period_start);
                            setPeriodEnd(g.period_end);
                            setStatementDate(
                              new Date().toISOString().split("T")[0]
                            );
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            border: isSelected
                              ? "1.5px solid #3a9e5f"
                              : "0.5px solid #e8e8e8",
                            background: isSelected ? "#eaf3de" : "#fafafa",
                            fontFamily: "inherit",
                            textAlign: "left",
                            transition: "all 0.1s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: isSelected ? "#3a9e5f" : "#d1d5db",
                                flexShrink: 0,
                                boxShadow: isSelected
                                  ? "0 0 0 3px rgba(58,158,95,0.15)"
                                  : "none",
                              }}
                            />
                            <span
                              className="t-name"
                              style={{ color: isSelected ? "#1a4731" : "#111" }}
                            >
                              {pLabel}
                            </span>
                          </div>
                          <span
                            className="t-label"
                            style={{
                              color: isSelected ? "#3a9e5f" : "#9ca3af",
                            }}
                          >
                            {g.statements.length}{" "}
                            {g.statements.length === 1 ? "driver" : "drivers"} ·{" "}
                            {fmt(g.totalGross)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "14px",
                  }}
                >
                  <div
                    style={{ flex: 1, height: "0.5px", background: "#e8e8e8" }}
                  />
                  <span
                    className="t-label"
                    style={{
                      color: "#9ca3af",
                      whiteSpace: "nowrap",
                      fontSize: "11px",
                    }}
                  >
                    or new period
                  </span>
                  <div
                    style={{ flex: 1, height: "0.5px", background: "#e8e8e8" }}
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Period start
                    </div>
                    <input
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Period end
                    </div>
                    <input
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Statement date
                    </div>
                    <input
                      type="date"
                      value={statementDate}
                      onChange={(e) => setStatementDate(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  padding: "20px",
                }}
              >
                <div className="t-name" style={{ marginBottom: "6px" }}>
                  Upload trips file
                </div>
                <div
                  className="t-label"
                  style={{ marginBottom: "16px", color: "#9ca3af" }}
                >
                  Excel file with columns: Key, Total Miles, Gross, Driver
                </div>
                <label
                  style={{
                    display: "block",
                    border: "2px dashed #e0e0e0",
                    borderRadius: "12px",
                    padding: "32px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = "#3a9e5f")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = "#e0e0e0")
                  }
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleXLS}
                    style={{ display: "none" }}
                  />
                  <div style={{ fontSize: "28px", marginBottom: "8px" }}>
                    📂
                  </div>
                  <div className="t-body" style={{ color: "#6b7280" }}>
                    Click to upload .xlsx or .xls file
                  </div>
                  <div
                    className="t-label"
                    style={{ color: "#9ca3af", marginTop: "4px" }}
                  >
                    All drivers in one file
                  </div>
                </label>
              </div>

              {fileUploaded && (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "14px 20px",
                      borderBottom: "0.5px solid #e8e8e8",
                      background: "#fafafa",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div className="t-table-header">
                        {Object.keys(uploadedGroups).length} drivers found
                      </div>
                      {(() => {
                        const dupCount = Object.entries(uploadedGroups).filter(
                          ([name]) => {
                            const m = matchDriver(name, drivers);
                            return m && duplicateMap[m.id];
                          }
                        ).length;
                        return dupCount > 0 ? (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "500",
                              padding: "2px 8px",
                              borderRadius: "20px",
                              background: "#faeeda",
                              color: "#633806",
                            }}
                          >
                            ⚠️ {dupCount} duplicate{dupCount > 1 ? "s" : ""}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      {unmatchedDrivers.length > 0 && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#854f0b",
                            background: "#faeeda",
                            padding: "3px 10px",
                            borderRadius: "20px",
                            fontWeight: "500",
                          }}
                        >
                          ⚠️ {unmatchedDrivers.length} unmatched
                        </div>
                      )}
                      {bulkMatchedEntries.length > 1 && (
                        <button
                          onClick={openBulkGenerate}
                          style={{
                            background: "#3a9e5f",
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: "500",
                            padding: "5px 12px",
                            borderRadius: "8px",
                            border: "none",
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          ⚡ Generate all {bulkMatchedEntries.length}
                        </button>
                      )}
                    </div>
                  </div>
                  {Object.entries(uploadedGroups).map(([driverName, loads]) => {
                    const matched = matchDriver(driverName, drivers);
                    const driverGross = loads.reduce((s, l) => s + l.gross, 0);
                    const driverNet = loads.reduce(
                      (s, l) => s + l.driver_net,
                      0
                    );
                    return (
                      <div
                        key={driverName}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 20px",
                          borderBottom: "0.5px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              background: matched ? "#eaf3de" : "#faeeda",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: "500",
                              color: matched ? "#3a9e5f" : "#854f0b",
                              flexShrink: 0,
                            }}
                          >
                            {driverName
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div>
                            <div className="t-name">{driverName}</div>
                            <div
                              className="t-label"
                              style={{ color: matched ? "#9ca3af" : "#854f0b" }}
                            >
                              {matched
                                ? `${loads.length} trips · Gross ${fmt(
                                    driverGross
                                  )}`
                                : "⚠️ Not found in drivers — add them first"}
                              {matched &&
                                duplicateMap[
                                  matchDriver(driverName, drivers)?.id
                                ] && (
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: "500",
                                      padding: "1px 6px",
                                      borderRadius: "20px",
                                      background: "#faeeda",
                                      color: "#633806",
                                      marginLeft: "4px",
                                    }}
                                  >
                                    ⚠️ Statement exists
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <div
                            className="t-data"
                            style={{ color: "#3b6d11", fontWeight: "500" }}
                          >
                            {fmt(driverNet)}
                          </div>
                          <button
                            onClick={() => {
                              if (!periodStart || !periodEnd) {
                                alert("Please set period start and end first");
                                return;
                              }
                              openDriver(driverName);
                            }}
                            disabled={!matched}
                            style={btnStyle(
                              !matched
                                ? "#f0f0f0"
                                : matchDriver(driverName, drivers) &&
                                  duplicateMap[
                                    matchDriver(driverName, drivers)?.id
                                  ]
                                ? "#faeeda"
                                : "#1a1e1c",
                              !matched
                                ? "#999"
                                : matchDriver(driverName, drivers) &&
                                  duplicateMap[
                                    matchDriver(driverName, drivers)?.id
                                  ]
                                ? "#633806"
                                : "#fff",
                              !matched
                            )}
                          >
                            Generate →
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* BULK — deductions table + progress + summary */}
          {view === "bulk" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Period info */}
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  padding: "14px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div className="t-name">
                    Bulk generate —{" "}
                    {fmtDate(periodStart, { month: "short", day: "numeric" })} –{" "}
                    {fmtDate(periodEnd, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <div
                    className="t-label"
                    style={{ color: "#9ca3af", marginTop: "2px" }}
                  >
                    {bulkMatchedEntries.length} drivers · Fixed deductions
                    editable below
                  </div>
                </div>
              </div>

              {/* Progress / summary shown after generate */}
              {bulkProgress.length > 0 && (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "20px",
                  }}
                >
                  <div className="t-name" style={{ marginBottom: "6px" }}>
                    {bulkDone
                      ? "All statements generated"
                      : "Generating statements..."}
                  </div>
                  {!bulkDone && (
                    <>
                      <div
                        style={{
                          background: "#f0f0f0",
                          borderRadius: "20px",
                          height: "6px",
                          margin: "12px 0",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            background: "#3a9e5f",
                            height: "6px",
                            borderRadius: "20px",
                            width: `${
                              (bulkProgress.filter(
                                (p) =>
                                  p.status === "done" || p.status === "error"
                              ).length /
                                bulkProgress.length) *
                              100
                            }%`,
                            transition: "width 0.3s",
                          }}
                        />
                      </div>
                      <div
                        className="t-label"
                        style={{
                          color: "#9ca3af",
                          textAlign: "center",
                          marginBottom: "16px",
                        }}
                      >
                        {bulkProgress.filter((p) => p.status === "done").length}{" "}
                        of {bulkProgress.length} done
                      </div>
                    </>
                  )}
                  {bulkDone && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "10px",
                        margin: "12px 0 16px",
                      }}
                    >
                      <div
                        style={{
                          background: "#f8f8f8",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}
                      >
                        <div
                          className="t-label"
                          style={{ marginBottom: "3px" }}
                        >
                          Total statements
                        </div>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            color: "#111",
                          }}
                        >
                          {
                            bulkProgress.filter((p) => p.status === "done")
                              .length
                          }
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#eaf3de",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}
                      >
                        <div
                          className="t-label"
                          style={{ color: "#3b6d11", marginBottom: "3px" }}
                        >
                          Telegram sent
                        </div>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            color: "#3b6d11",
                          }}
                        >
                          {
                            bulkProgress.filter(
                              (p) => p.status === "done" && p.hasTelegram
                            ).length
                          }
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#faeeda",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}
                      >
                        <div
                          className="t-label"
                          style={{ color: "#854f0b", marginBottom: "3px" }}
                        >
                          No Telegram
                        </div>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            color: "#854f0b",
                          }}
                        >
                          {
                            bulkProgress.filter(
                              (p) => p.status === "done" && !p.hasTelegram
                            ).length
                          }
                        </div>
                      </div>
                    </div>
                  )}
                  {bulkProgress.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 0",
                        borderBottom:
                          i < bulkProgress.length - 1
                            ? "0.5px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          flexShrink: 0,
                          background:
                            p.status === "done"
                              ? "#eaf3de"
                              : p.status === "generating"
                              ? "#faeeda"
                              : p.status === "error"
                              ? "#fcebeb"
                              : "#f0f0f0",
                          color:
                            p.status === "done"
                              ? "#3a9e5f"
                              : p.status === "generating"
                              ? "#854f0b"
                              : p.status === "error"
                              ? "#a32d2d"
                              : "#9ca3af",
                        }}
                      >
                        {p.status === "done"
                          ? "✓"
                          : p.status === "generating"
                          ? "⏳"
                          : p.status === "error"
                          ? "✗"
                          : "·"}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="t-name">{p.name}</div>
                        <div className="t-label" style={{ color: "#9ca3af" }}>
                          {p.status === "done"
                            ? `Net pay ${fmt(p.netPay)} · ${
                                p.hasTelegram
                                  ? "Telegram sent"
                                  : "Saved (no Telegram)"
                              }`
                            : p.status === "generating"
                            ? "Generating..."
                            : p.status === "error"
                            ? "Error — check logs"
                            : "Waiting..."}
                        </div>
                      </div>
                      {p.status === "done" && (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            background: p.hasTelegram ? "#eaf3de" : "#faeeda",
                            color: p.hasTelegram ? "#3b6d11" : "#854f0b",
                          }}
                        >
                          {p.hasTelegram ? "sent" : "saved"}
                        </span>
                      )}
                    </div>
                  ))}
                  {bulkDone && (
                    <div
                      style={{ display: "flex", gap: "8px", marginTop: "16px" }}
                    >
                      <button
                        onClick={() => setView("list")}
                        style={btnStyle("#1a1e1c", "#fff")}
                      >
                        View statements
                      </button>
                      <button
                        onClick={() => {
                          setView("new");
                          setBulkProgress([]);
                          setBulkDone(false);
                        }}
                        style={btnStyle("#f0f0f0", "#555")}
                      >
                        Generate another period
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Deductions table — only shown before generating */}
              {!bulkGenerating && !bulkDone && (
                <>
                  <div
                    style={{
                      background: "#fff",
                      border: "0.5px solid #e8e8e8",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "12px 16px",
                        background: "#fafafa",
                        borderBottom: "0.5px solid #e8e8e8",
                      }}
                    >
                      <div className="t-name" style={{ marginBottom: "2px" }}>
                        Deductions per driver
                      </div>
                      <div className="t-label" style={{ color: "#9ca3af" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            borderRadius: "2px",
                            background: "#eaf3de",
                            border: "1px solid #b7e0c4",
                            marginRight: "5px",
                            verticalAlign: "middle",
                          }}
                        ></span>
                        Green = fixed (pre-filled, editable per driver) · White
                        = variable
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "12px",
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: "0.5px solid #e8e8e8" }}>
                            <th
                              style={{
                                padding: "8px 10px",
                                textAlign: "left",
                                fontSize: "10px",
                                fontWeight: "500",
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                whiteSpace: "nowrap",
                                background: "#fafafa",
                              }}
                            >
                              Driver
                            </th>
                            {["IFTA", "Insurance", "Samsara", "Safety"].map(
                              (h) => (
                                <th
                                  key={h}
                                  style={{
                                    padding: "8px 10px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    fontWeight: "500",
                                    color: "#3b6d11",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    whiteSpace: "nowrap",
                                    background: "#f0faf4",
                                  }}
                                >
                                  {h}
                                </th>
                              )
                            )}
                            {["Fuel gross", "Fuel disc"].map((h) => (
                              <th
                                key={h}
                                style={{
                                  padding: "8px 10px",
                                  textAlign: "center",
                                  fontSize: "10px",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  whiteSpace: "nowrap",
                                  background: "#fafafa",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                            <th
                              style={{
                                padding: "8px 10px",
                                textAlign: "center",
                                fontSize: "10px",
                                fontWeight: "500",
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                whiteSpace: "nowrap",
                                background: "#fafafa",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <button
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const {
                                      data: tollData,
                                    } = await supabase
                                      .from("fuel_toll_imports")
                                      .select("driver_id, toll")
                                      .eq("type", "toll")
                                      .order("created_at", {
                                        ascending: false,
                                      });
                                    if (!tollData) return;
                                    const tollMap = {};
                                    tollData.forEach((r) => {
                                      if (r.driver_id && !tollMap[r.driver_id])
                                        tollMap[r.driver_id] = parseFloat(
                                          parseFloat(r.toll || 0).toFixed(2)
                                        );
                                    });
                                    bulkMatchedEntries.forEach(([name]) => {
                                      const matched = matchDriver(
                                        name,
                                        drivers
                                      );
                                      if (matched && tollMap[matched.id])
                                        updateBulkDed(
                                          name,
                                          "toll",
                                          tollMap[matched.id]
                                        );
                                    });
                                  }}
                                  style={{
                                    background: "#eaf3de",
                                    color: "#3b6d11",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    fontSize: "9px",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  ↓ Pull
                                </button>
                                <span>Toll</span>
                              </div>
                            </th>
                            {[
                              "Other desc",
                              "Other $",
                              "Reimb desc",
                              "Reimb $",
                            ].map((h) => (
                              <th
                                key={h}
                                style={{
                                  padding: "8px 10px",
                                  textAlign: "center",
                                  fontSize: "10px",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  whiteSpace: "nowrap",
                                  background: "#fafafa",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bulkMatchedEntries.map(([driverName, loads], i) => {
                            const driverGross = loads.reduce(
                              (s, l) => s + l.gross,
                              0
                            );
                            const deds = bulkDeds[driverName] || {};
                            const rowBg = i % 2 === 0 ? "#fff" : "#fafafa";
                            const fixedBg = i % 2 === 0 ? "#f5fbf7" : "#eef8f2";
                            return (
                              <tr
                                key={driverName}
                                style={{ borderBottom: "0.5px solid #f0f0f0" }}
                              >
                                <td
                                  style={{
                                    padding: "8px 10px",
                                    minWidth: "140px",
                                    background: rowBg,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "7px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                        background: "#eaf3de",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "9px",
                                        fontWeight: "500",
                                        color: "#3a9e5f",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {driverName
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .slice(0, 2)
                                        .toUpperCase()}
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "500",
                                          color: "#111",
                                        }}
                                      >
                                        {driverName}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {fmt(driverGross)}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                {[
                                  {
                                    key: "ifta_eld",
                                    def: DEFAULT_DEDUCTIONS.ifta_eld,
                                  },
                                  {
                                    key: "insurance",
                                    def: DEFAULT_DEDUCTIONS.insurance,
                                  },
                                  {
                                    key: "samsara",
                                    def: DEFAULT_DEDUCTIONS.samsara,
                                  },
                                  {
                                    key: "safety",
                                    def: DEFAULT_DEDUCTIONS.safety,
                                  },
                                ].map(({ key, def }) => (
                                  <td
                                    key={key}
                                    style={{
                                      padding: "6px 8px",
                                      background: fixedBg,
                                    }}
                                  >
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      defaultValue={
                                        deds[key] !== undefined
                                          ? deds[key]
                                          : def
                                      }
                                      style={{
                                        ...miniInputStyle,
                                        border: "0.5px solid #b7e0c4",
                                        background: "transparent",
                                      }}
                                      onChange={(e) => {
                                        const v =
                                          parseFloat(e.target.value) || 0;
                                        updateBulkDed(driverName, key, v);
                                      }}
                                    />
                                  </td>
                                ))}
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    defaultValue={deds.fuel_gross || ""}
                                    key={`${driverName}-fg-${deds.fuel_gross}`}
                                    style={miniInputStyle}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value) || 0;
                                      updateBulkDed(
                                        driverName,
                                        "fuel_gross",
                                        v
                                      );
                                    }}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    defaultValue={deds.fuel_discount || ""}
                                    key={`${driverName}-fd-${deds.fuel_discount}`}
                                    style={miniInputStyle}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value) || 0;
                                      updateBulkDed(
                                        driverName,
                                        "fuel_discount",
                                        v
                                      );
                                    }}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    defaultValue={deds.toll || ""}
                                    key={`${driverName}-tl-${deds.toll}`}
                                    style={miniInputStyle}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value) || 0;
                                      updateBulkDed(driverName, "toll", v);
                                    }}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="e.g. Advance"
                                    defaultValue=""
                                    style={{ ...miniTextStyle, width: "90px" }}
                                    onChange={(e) =>
                                      updateBulkDed(
                                        driverName,
                                        "other_description",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    defaultValue=""
                                    style={miniInputStyle}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value) || 0;
                                      updateBulkDed(
                                        driverName,
                                        "other_amount",
                                        v
                                      );
                                    }}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="e.g. Parking"
                                    defaultValue=""
                                    style={{ ...miniTextStyle, width: "90px" }}
                                    onChange={(e) =>
                                      updateBulkDed(
                                        driverName,
                                        "reimbursement_description",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    background: rowBg,
                                  }}
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    defaultValue=""
                                    style={miniInputStyle}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value) || 0;
                                      updateBulkDed(
                                        driverName,
                                        "reimbursement_amount",
                                        v
                                      );
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Summary bar */}
                  <div
                    style={{
                      background: "#1a1e1c",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr 1fr"
                          : "repeat(4, 1fr)",
                        gap: "16px",
                      }}
                    >
                      {[
                        {
                          label: "Drivers",
                          value: String(bulkMatchedEntries.length),
                          color: "#fff",
                        },
                        {
                          label: "Total gross",
                          value: fmt(bulkTotalGross),
                          color: "#fff",
                        },
                        {
                          label: "Total deductions",
                          value: `-${fmt(Math.max(0, bulkTotalDeds))}`,
                          color: "#f87171",
                        },
                        {
                          label: "Total net pay",
                          value: fmt(bulkTotalNet),
                          color: "#3dcc82",
                        },
                      ].map(({ label, value, color }) => (
                        <div key={label}>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "rgba(255,255,255,0.4)",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              marginBottom: "4px",
                            }}
                          >
                            {label}
                          </div>
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "600",
                              letterSpacing: "-0.02em",
                              color,
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={runBulkGenerate}
                      style={btnStyle("#3a9e5f", "#fff")}
                    >
                      ⚡ Generate all {bulkMatchedEntries.length} statements
                    </button>
                    <button
                      onClick={() => setView("new")}
                      style={btnStyle("#f0f0f0", "#555")}
                    >
                      ← Back
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* GENERATE — single driver */}
          {view === "generate" && activeDriverName && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  padding: "16px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: "#eaf3de",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#3a9e5f",
                    flexShrink: 0,
                  }}
                >
                  {activeDriverName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div>
                  <div className="t-name" style={{ fontSize: "14px" }}>
                    {activeDriverName}
                  </div>
                  <div className="t-label" style={{ color: "#9ca3af" }}>
                    {activeDriver?.corp_name
                      ? `Pay to: ${activeDriver.corp_name}`
                      : "Pay to: driver name"}{" "}
                    · {activeLoads.length} trips
                  </div>
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "14px 20px",
                    borderBottom: "0.5px solid #e8e8e8",
                    background: "#fafafa",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="t-table-header">
                    Trips ({activeLoads.length})
                  </div>
                  <div className="t-label" style={{ color: "#3a9e5f" }}>
                    Total gross: {fmt(totalGross)}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#fafafa" }}>
                        {[
                          "Trip ID",
                          "Pickup",
                          "Dropoff",
                          "Miles",
                          "Gross",
                          "Rate",
                          "Driver net",
                        ].map((h) => (
                          <th
                            key={h}
                            className="t-table-header"
                            style={{
                              padding: "9px 12px",
                              textAlign:
                                h === "Trip ID" ||
                                h === "Pickup" ||
                                h === "Dropoff"
                                  ? "left"
                                  : "right",
                              fontWeight: "500",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeLoads.map((load, i) => (
                        <tr
                          key={i}
                          style={{
                            borderTop: "0.5px solid #f0f0f0",
                            background: i % 2 === 1 ? "#fafafa" : "#fff",
                          }}
                        >
                          <td
                            className="t-data"
                            style={{ padding: "9px 12px" }}
                          >
                            {load.trip_id}
                          </td>
                          <td
                            className="t-body"
                            style={{ padding: "9px 12px", color: "#6b7280" }}
                          >
                            {load.pickup || "—"}
                          </td>
                          <td
                            className="t-body"
                            style={{ padding: "9px 12px", color: "#6b7280" }}
                          >
                            {load.dropoff || "—"}
                          </td>
                          <td
                            className="t-data"
                            style={{ padding: "9px 12px", textAlign: "right" }}
                          >
                            {load.miles}
                          </td>
                          <td
                            className="t-data"
                            style={{ padding: "9px 12px", textAlign: "right" }}
                          >
                            {fmt(load.gross)}
                          </td>
                          <td
                            className="t-data"
                            style={{
                              padding: "9px 12px",
                              textAlign: "right",
                              color: "#6b7280",
                            }}
                          >
                            90%
                          </td>
                          <td
                            className="t-data"
                            style={{
                              padding: "9px 12px",
                              textAlign: "right",
                              color: "#3b6d11",
                              fontWeight: "500",
                            }}
                          >
                            {fmt(load.driver_net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          borderTop: "1px solid #e8e8e8",
                          background: "#fafafa",
                        }}
                      >
                        <td
                          colSpan={4}
                          className="t-label"
                          style={{ padding: "9px 12px" }}
                        >
                          Totals
                        </td>
                        <td
                          className="t-data"
                          style={{
                            padding: "9px 12px",
                            textAlign: "right",
                            fontWeight: "600",
                          }}
                        >
                          {fmt(totalGross)}
                        </td>
                        <td style={{ padding: "9px 12px" }}></td>
                        <td
                          className="t-data"
                          style={{
                            padding: "9px 12px",
                            textAlign: "right",
                            color: "#3b6d11",
                            fontWeight: "600",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmt(totalDriverNet)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  padding: "20px",
                }}
              >
                <div className="t-name" style={{ marginBottom: "16px" }}>
                  Deductions
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 1fr"
                      : "repeat(3, 1fr)",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  {[
                    { key: "ifta_eld", label: "IFTA / ELD" },
                    { key: "insurance", label: "Insurance" },
                    { key: "samsara", label: "Samsara" },
                    { key: "safety", label: "Safety" },
                    { key: "toll", label: "Toll" },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <div className="t-label" style={{ marginBottom: "4px" }}>
                        {label}
                      </div>
                      {dedInput(deductions[key], key, setDeductions)}
                    </div>
                  ))}
                </div>
                <FuelFields
                  deds={deductions}
                  setDeds={setDeductions}
                  netFuelVal={liveNetFuel}
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Other deduction
                    </div>
                    <input
                      value={deductions.other_description}
                      onChange={(e) =>
                        setDeductions((p) => ({
                          ...p,
                          other_description: e.target.value,
                        }))
                      }
                      placeholder="e.g. Advance"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Amount
                    </div>
                    {dedInput(
                      deductions.other_amount,
                      "other_amount",
                      setDeductions
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Reimbursement
                    </div>
                    <input
                      value={deductions.reimbursement_description}
                      onChange={(e) =>
                        setDeductions((p) => ({
                          ...p,
                          reimbursement_description: e.target.value,
                        }))
                      }
                      placeholder="e.g. Parking refund"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <div className="t-label" style={{ marginBottom: "4px" }}>
                      Amount
                    </div>
                    {dedInput(
                      deductions.reimbursement_amount,
                      "reimbursement_amount",
                      setDeductions
                    )}
                  </div>
                </div>
              </div>
              <div
                style={{
                  background: "#1a1e1c",
                  borderRadius: "12px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 1fr"
                      : "repeat(4, 1fr)",
                    gap: "16px",
                  }}
                >
                  {[
                    {
                      label: "Total gross",
                      value: fmt(totalGross),
                      color: "#fff",
                    },
                    {
                      label: "Porso fee (10%)",
                      value: `-${fmt(totalPorsoFee)}`,
                      color: "#f87171",
                    },
                    {
                      label: "Total deductions",
                      value: `-${fmt(totalFixedDeductions)}`,
                      color: "#f87171",
                    },
                    { label: "Net pay", value: fmt(netPay), color: "#3dcc82" },
                  ].map(({ label, value, color }) => (
                    <div key={label}>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "rgba(255,255,255,0.4)",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: "4px",
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          letterSpacing: "-0.02em",
                          color,
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={generatePDF}
                  disabled={generating}
                  style={btnStyle("#3a9e5f", "#fff", generating)}
                >
                  {generating
                    ? "Generating..."
                    : "📄 Generate & send statement"}
                </button>
                <button
                  onClick={() => setView("new")}
                  style={btnStyle("#f0f0f0", "#555")}
                >
                  ← Back to drivers
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
